import React, { useContext } from 'react';
import { Field, Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { object as YupObject, string as YupString } from 'yup';

import { emailValidationRegex } from 'app/shared/utils/emailValidation';
import { get } from 'app/shared/utils/get';
import { Ticket } from 'app/shared/typings/tickets';
import { AuthContext } from 'app/shared/context/Auth';
import { Box, Typography } from 'app/shared/components/atoms';
import { TermsAndConditions, Textfield } from 'app/shared/components/molecules';
import {
  ModalButtonsSection,
  ModalDetailsSection,
  ModalTitle,
} from 'app/shared/components/molecules';
import { UpdateTicket } from 'app/fan/graphql/tickets/mutationHooks';
import { useEventPageContext } from 'app/fan/context/EventPage';

interface Props {
  onSuccess: (ticket: Ticket) => void;
}

export const PlusOneTicketInfoForm: React.FC<
  React.PropsWithChildren<Props>
> = ({ onSuccess }) => {
  const { formatMessage } = useIntl();
  const { user } = useContext(AuthContext);
  const { eventAttendee } = useEventPageContext();
  const updateTicket = UpdateTicket();

  const PlusOneTicketInfoFormSchema = YupObject().shape({
    firstName: YupString(),
    lastName: YupString(),
    email: YupString()
      .matches(emailValidationRegex, {
        message: formatMessage(
          {
            id: 'form.fieldMustBeValidEmail',
          },
          {
            field: 'Email',
          }
        ),
      })
      .required(
        formatMessage({ id: 'event.plusOneFlow.ticketInfo.emailRequired' })
      ),
  });

  if (!eventAttendee) {
    return null;
  }

  return (
    <Box flexDirection="column" gap={8}>
      <ModalTitle
        title={formatMessage({ id: 'event.plusOneFlow.ticketInfo.title' })}
      />
      <Typography>
        {formatMessage({ id: 'event.plusOneFlow.ticketInfo.body' })}
      </Typography>
      <Formik
        initialValues={{
          email: user?.email || '',
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
        }}
        validationSchema={PlusOneTicketInfoFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          const response = await updateTicket({
            variables: {
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              eventAttendeeId: Number(eventAttendee.id),
            },
          });

          if (response.data) {
            const ticket = get(response.data, 'updateTicket.ticket', null);

            setStatus({ success: true });
            setSubmitting(false);
            onSuccess(ticket);
          } else {
            setStatus({
              success: false,
              error: formatMessage({
                id: 'event.plusOneFlow.ticketInfo.networkError',
              }),
            });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          values,
          touched,
          setFieldError,
        }) => {
          const errorMsg = errors.email && touched.email && errors.email;

          return (
            <Form translate="yes">
              <Box flexDirection="column" gap={8}>
                <Field
                  as={Textfield}
                  data-qaid="plus-one-ticket-info-first-name"
                  label={formatMessage({
                    id: 'event.plusOneFlow.ticketInfo.firstName',
                  })}
                  onChange={handleChange}
                  value={values.firstName}
                  name="firstName"
                />

                <Field
                  as={Textfield}
                  data-qaid="plus-one-ticket-info-last-name"
                  label={formatMessage({
                    id: 'event.plusOneFlow.ticketInfo.lastName',
                  })}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lastName}
                  name="lastName"
                />

                <Field
                  as={Textfield}
                  data-qaid="plus-one-ticket-info-email"
                  label={formatMessage({
                    id: 'event.plusOneFlow.ticketInfo.email',
                  })}
                  onChange={(e: any) => {
                    setFieldError('email', undefined);
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  hasError={!!errorMsg}
                  helperText={errorMsg}
                  value={values.email}
                  type="email"
                  name="email"
                  autoCapitalize="off"
                />

                <ModalButtonsSection
                  primaryCtaText={formatMessage({
                    id: 'event.plusOneFlow.ticketInfo.cta',
                  })}
                  primaryCtaType="submit"
                  isPrimaryCtaLoading={isSubmitting}
                  isPrimaryCtaDisabled={!!errorMsg || !values.email}
                  primaryCtaDataQaid="plus-one-ticket-info-submit"
                  isPrimaryCtaFullWidth={true}
                />
              </Box>
            </Form>
          );
        }}
      </Formik>
      <ModalDetailsSection as="div" detailText={<TermsAndConditions />} />
    </Box>
  );
};
